"use client";
import React, { useEffect, useState } from "react";

import plus from "@/assets/icons/plus.svg";
import minus from "@/assets/icons/minus.svg";
import ticket from "@/assets/icons/ticket-2.svg";
import Image from "next/image";
import EventDetailsCard from "@/components/shared/EventDetailsCard";
import Link from "next/link";
import { fetchEventDetails, filterTickets, holdTicket } from "@/utils/loaders";
import { useParams, useSearchParams } from "next/navigation";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import SelectTicketDate from "@/components/SelectTicketDate";
import Loading from "@/components/layout/Loading";
import { useTranslations } from "next-intl";

export default function Page() {
  const t = useTranslations("business");
  const { id: routeId }: any = useParams();
  const id = String(routeId || "").split("--")[0];
  const searchParams = useSearchParams();
  const queryString = searchParams.toString();
  const [eventDetails, setEventDetails] = useState<any>(null);
  const [tickets, setTickets] = useState<any>([]);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [selectedTime, setSelectedTime] = useState<any>("");
  const [showSelectDate, setShowSelectDate] = useState(false);
  const [loading, setLoading] = useState(true);
  const safeTickets = Array.isArray(tickets) ? tickets : [];

  // timer
  useEffect(() => {
    const timer = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      } else if (minutes > 0) {
        setMinutes(minutes - 1);
        setSeconds(59);
      } else {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [minutes, seconds]);

  const validateSellingMethod = (
    ticket: any,
    newCount: number,
  ): string | null => {
    const sellingMethod = ticket?.selling_method;
    const remaining = ticket?.remaining || 0;

    if (!sellingMethod) {
      return null; // No restrictions
    }

    // no_split_sell_all_together: Must buy all remaining tickets
    if (sellingMethod === "no_split_sell_all_together") {
      if (newCount !== remaining) {
        return t("mustBuyAllTickets");
      }
    }

    // sell_in_pairs: Must buy even number of tickets
    if (sellingMethod === "sell_in_pairs") {
      if (newCount > 0 && newCount % 2 !== 0) {
        return t("mustBuyEvenNumber");
      }
    }

    // any_quantity_single_ticket_only: Can't leave one ticket remaining
    if (sellingMethod === "any_quantity_single_ticket_only") {
      const ticketsLeftAfterPurchase = remaining - newCount;
      if (ticketsLeftAfterPurchase === 1) {
        return t("cannotLeaveOneTicket");
      }
    }

    return null;
  };

  const handleCountChange = (index: number, delta: number) => {
    const currentTicket = safeTickets[index];

    if (!currentTicket) {
      return;
    }

    const currentCount = currentTicket.count || 0;
    const max = currentTicket.remaining;
    const newCount = currentCount + delta;
    const ticket = currentTicket;

    if (newCount < 0) {
      toast.error(t("selectAtLeastOneTicket"));
      return;
    }
    if (newCount > max) {
      toast.error(t("exceededMaxTickets"));
      return;
    }

    // Validate selling method restrictions
    const validationError = validateSellingMethod(ticket, newCount);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Update the count for the specific ticket
    const updatedTickets = [...safeTickets];
    updatedTickets?.map((res: any) => (res.count = 0));
    updatedTickets[index].count = newCount;
    setTickets(updatedTickets);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const eventDetails = await fetchEventDetails(id);

      setEventDetails(eventDetails?.data);
      setShowSelectDate(true);
      setLoading(false);
    };
    fetchData();
  }, []);

  const slugify = (value: string) => {
    return value
      .trim()
      .replace(/[\s_]+/g, "-")
      .replace(/[^\p{L}\p{N}-]+/gu, "")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  useEffect(() => {
    const name =
      eventDetails?.event?.name ??
      eventDetails?.name ??
      eventDetails?.event_name ??
      eventDetails?.event?.event_name;
    const rawSlug =
      eventDetails?.event?.slug ??
      eventDetails?.slug ??
      eventDetails?.event_slug;
    const fullSlug =
      rawSlug && rawSlug.includes("--")
        ? rawSlug
        : rawSlug
          ? `${id}--${rawSlug}`
          : name
            ? `${id}--${slugify(String(name))}`
            : null;
    const slug = fullSlug;
    if (!slug || !routeId) return;
    if (String(routeId) === String(slug)) return;
    if (String(routeId).includes("--")) return;
    const nextUrl = `/tickets/${slug}/select-ticket/business${
      queryString ? `?${queryString}` : ""
    }`;
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", nextUrl);
    }
  }, [
    eventDetails?.event?.name,
    eventDetails?.event?.slug,
    eventDetails?.name,
    eventDetails?.slug,
    eventDetails?.event_name,
    eventDetails?.event?.event_name,
    eventDetails?.event_slug,
    id,
    queryString,
    routeId,
  ]);

  const handleSubmit = async (e?: React.MouseEvent) => {
    // Prevent navigation if validation fails
    if (e) {
      e.preventDefault();
    }

    // Check if any tickets are selected
    if (totalTickets === 0) {
      toast.error(t("selectAtLeastOneTicket"));
      return;
    }

    const selectedTickets = safeTickets.filter(
      (ticket: any) => ticket?.count > 0,
    );
    const selectedTicket = selectedTickets[0];

    if (!selectedTicket) {
      toast.error(t("selectAtLeastOneTicket"));
      return;
    }

    // Validate selling method restrictions before submitting
    for (const ticket of selectedTickets || []) {
      const validationError = validateSellingMethod(ticket, ticket.count);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    const data = await holdTicket({
      ticket_id: selectedTicket.id,
      ticket_no: selectedTicket.count,
      one_box: selectedTicket.onebox,
    });
    if (data?.success) {
      Cookies.set("time", data?.data?.time, {
        expires: 1,
      });
      Cookies.set("selectedTicket", JSON.stringify(selectedTicket), {
        expires: 1,
      });
      Cookies.set("event-id", id, {
        expires: 1,
      });
      // Navigate to payment page
      window.location.href = "/payment?step=2";
    } else {
      data?.message && toast.error(data?.message);
    }
  };

  const totalTickets = safeTickets.reduce(
    (sum: any, t: any) => sum + (t?.count || 0),
    0,
  );
  const totalPrice = safeTickets.reduce(
    (sum: any, t: any) =>
      sum + (t?.count || 0) * (t?.price_after_commission || 0),
    0,
  );
  if (loading) {
    return <Loading />;
  }

  return (
    <div className="container mx-auto px-2 lg:px-20 flex flex-col items-center py-6">
      {/* Event Details */}
      <EventDetailsCard data={eventDetails?.event} />

      <div className="w-full flex flex-col gap-6 mt-5 lg:mt-0 mb-10">
        {/* عادية */}
        <div className="glass-effect border border-[#d9ac4f1F] rounded-[16px] lg:bg-transparent lg:border-none py-3 ">
          {/* <div className="font-extrabold text-[22px] mb-7">تذاكر عادية</div> */}
          {safeTickets.map((ticket: any, index: number) => (
            <div className="lg:glass-effect rounded-[10px] lg:border border-[#2F375F1A] lg:shadow-[10px_10px_25.3px_0px_rgba(0,0,0,0.05)] px-4 lg:px-10 lg:py-7 flex items-center justify-between mb-5 lg:mb-6">
              <div className="text-right">
                <div className="font-medium text-xs lg:text-[18px] text-[#D1D3D4] mb-1 lg:mb-3">
                  {ticket?.tag?.name}
                </div>
                <div
                  className="text-sm lg:text-[20px] font-extrabold flex items-center gap-2"
                  dir="ltr"
                >
                  <svg
                    width="20"
                    height="22"
                    viewBox="0 0 20 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-[15px] lg:w-[20px] mb-1"
                  >
                    <g clipPath="url(#clip0_55_22959)">
                      <path
                        d="M8.75922 0.312267C8.18771 0.699417 7.57319 1.22176 7.25978 1.58433L6.9771 1.91003L6.94637 6.53126L6.91564 11.1525L4.41453 11.681C3.03799 11.976 1.87039 12.2279 1.82123 12.2463C1.66145 12.3078 1.22514 13.6843 1.13296 14.4156C1.09609 14.7352 1.10224 14.7598 1.20671 14.7229C1.36034 14.6799 6.82347 13.5184 6.89721 13.5184C6.92179 13.5184 6.94023 14.1882 6.93408 15.0056L6.91564 16.4927L3.81844 17.1503C1.56313 17.6296 0.69665 17.8385 0.641343 17.9123C0.444695 18.1827 -0.0653607 20.1676 0.0329633 20.2659C0.0636896 20.3028 0.389388 20.2352 4.54972 19.3503C6.97095 18.8341 7.24134 18.7419 7.61006 18.3486C7.70838 18.2441 8.10168 17.691 8.48883 17.1195L9.18939 16.0871L9.22011 14.5447L9.25084 13.0022L10.3263 12.7748C10.9162 12.6458 11.4385 12.5413 11.4816 12.5413C11.5369 12.5352 11.5553 13.0391 11.5553 14.8704C11.5553 17.0519 11.5615 17.2056 11.6659 17.2056C11.8564 17.2056 18.948 15.6754 18.9911 15.6262C19.1508 15.448 19.5134 14.1944 19.6363 13.4201L19.6732 13.1681L18.8866 13.3341C15.562 14.0408 13.9642 14.3787 13.9274 14.3787C13.9028 14.3787 13.8966 13.8441 13.9028 13.1989L13.9212 12.0128L16.4346 11.4782C17.8173 11.1894 18.9665 10.9251 18.9911 10.8944C19.1508 10.7162 19.5134 9.46869 19.6363 8.68824L19.6732 8.43629L16.8587 9.03852C15.3101 9.37037 14.0073 9.64076 13.9704 9.6469C13.9089 9.6469 13.8905 8.75584 13.8905 5.40054V1.15417L13.6631 1.29551C13.1715 1.58433 12.4894 2.13126 12.0346 2.5983L11.5553 3.08378L11.543 6.62344L11.5246 10.1693L10.4799 10.3966C9.90838 10.5195 9.38603 10.624 9.33073 10.624C9.22626 10.6301 9.22011 10.3413 9.22011 5.34523C9.22011 2.43852 9.20168 0.0603123 9.1771 0.0603123C9.14637 0.0603123 8.96201 0.177071 8.75922 0.312267Z"
                        fill="black"
                      />
                      <path
                        d="M18.2554 18.1953C17.4934 18.3612 15.8772 18.7054 14.6604 18.9573C13.4437 19.2154 12.4051 19.4367 12.3498 19.4551C12.1654 19.5043 11.809 20.5121 11.6615 21.4154C11.6001 21.778 11.5939 21.9132 11.6492 21.9255C11.7476 21.9501 18.9437 20.4138 18.999 20.3585C19.1526 20.2048 19.7487 17.9864 19.6565 17.9188C19.6442 17.9065 19.0174 18.0355 18.2554 18.1953Z"
                        fill="black"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_55_22959">
                        <rect width="19.6648" height="22" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  {ticket?.price_after_commission}{" "}
                </div>
              </div>
              <div className="flex flex-row-reverse justify-between items-center gap-2 w-full max-w-[100px] lg:max-w-[150px]">
                <button
                  disabled={safeTickets[index]?.count === ticket?.remaining}
                  onClick={(e) => {
                    handleCountChange(index, 1);
                  }}
                  className="w-[54px] h-[30px] lg:h-[49px] rounded-lg flex items-center justify-center bg-[#d9ac4f] disabled:bg-[#d9ac4f]/50 disabled:cursor-not-allowed"
                >
                  <Image src={plus} alt="" className="w-[14px] lg:w-[20px]" />
                </button>

                <span className="lg:mx-2 w-[10px] text-center">
                  {safeTickets[index]?.count || 0}
                </span>
                <button
                  disabled={safeTickets[index]?.count === 0}
                  onClick={() => handleCountChange(index, -1)}
                  className="w-[54px] h-[30px] lg:h-[49px] rounded-lg flex items-center justify-center bg-[#d9ac4f] disabled:bg-[#d9ac4f]/50 disabled:cursor-not-allowed"
                >
                  <Image src={minus} alt="" className="w-[12px] lg:w-[20px]" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Order Summary Bar */}
      <div className="fixed bottom-0 left-0 right-0 glass-effect  items-center justify-center  z-50 hidden lg:flex">
        <div className="w-full   flex flex-col-reverse gap-3 lg:flex-row-reverse items-center justify-between container mx-auto px-4   py-4">
          <div className="flex items-center gap-7">
            <div
              className="text-[#D1D3D4] text-[20px] font-medium flex items-center gap-2"
              dir="ltr"
            >
              <svg
                width="20"
                height="22"
                viewBox="0 0 20 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_55_22959)">
                  <path
                    d="M8.75922 0.312267C8.18771 0.699417 7.57319 1.22176 7.25978 1.58433L6.9771 1.91003L6.94637 6.53126L6.91564 11.1525L4.41453 11.681C3.03799 11.976 1.87039 12.2279 1.82123 12.2463C1.66145 12.3078 1.22514 13.6843 1.13296 14.4156C1.09609 14.7352 1.10224 14.7598 1.20671 14.7229C1.36034 14.6799 6.82347 13.5184 6.89721 13.5184C6.92179 13.5184 6.94023 14.1882 6.93408 15.0056L6.91564 16.4927L3.81844 17.1503C1.56313 17.6296 0.69665 17.8385 0.641343 17.9123C0.444695 18.1827 -0.0653607 20.1676 0.0329633 20.2659C0.0636896 20.3028 0.389388 20.2352 4.54972 19.3503C6.97095 18.8341 7.24134 18.7419 7.61006 18.3486C7.70838 18.2441 8.10168 17.691 8.48883 17.1195L9.18939 16.0871L9.22011 14.5447L9.25084 13.0022L10.3263 12.7748C10.9162 12.6458 11.4385 12.5413 11.4816 12.5413C11.5369 12.5352 11.5553 13.0391 11.5553 14.8704C11.5553 17.0519 11.5615 17.2056 11.6659 17.2056C11.8564 17.2056 18.948 15.6754 18.9911 15.6262C19.1508 15.448 19.5134 14.1944 19.6363 13.4201L19.6732 13.1681L18.8866 13.3341C15.562 14.0408 13.9642 14.3787 13.9274 14.3787C13.9028 14.3787 13.8966 13.8441 13.9028 13.1989L13.9212 12.0128L16.4346 11.4782C17.8173 11.1894 18.9665 10.9251 18.9911 10.8944C19.1508 10.7162 19.5134 9.46869 19.6363 8.68824L19.6732 8.43629L16.8587 9.03852C15.3101 9.37037 14.0073 9.64076 13.9704 9.6469C13.9089 9.6469 13.8905 8.75584 13.8905 5.40054V1.15417L13.6631 1.29551C13.1715 1.58433 12.4894 2.13126 12.0346 2.5983L11.5553 3.08378L11.543 6.62344L11.5246 10.1693L10.4799 10.3966C9.90838 10.5195 9.38603 10.624 9.33073 10.624C9.22626 10.6301 9.22011 10.3413 9.22011 5.34523C9.22011 2.43852 9.20168 0.0603123 9.1771 0.0603123C9.14637 0.0603123 8.96201 0.177071 8.75922 0.312267Z"
                    fill="black"
                  />
                  <path
                    d="M18.2554 18.1953C17.4934 18.3612 15.8772 18.7054 14.6604 18.9573C13.4437 19.2154 12.4051 19.4367 12.3498 19.4551C12.1654 19.5043 11.809 20.5121 11.6615 21.4154C11.6001 21.778 11.5939 21.9132 11.6492 21.9255C11.7476 21.9501 18.9437 20.4138 18.999 20.3585C19.1526 20.2048 19.7487 17.9864 19.6565 17.9188C19.6442 17.9065 19.0174 18.0355 18.2554 18.1953Z"
                    fill="black"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_55_22959">
                    <rect width="19.6648" height="22" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <span className="font-extrabold">
                {totalPrice?.toFixed(2)}
              </span>{" "}
            </div>
            <Link
              href={"/payment?step=2"}
              onClick={handleSubmit}
              className="bg-[#d9ac4f] text-[#D1D3D4] text-[20px] font-bold rounded-md px-13 py-3 text-lg hover:bg-yellow-600 transition-colors"
            >
              {t("payment")}
            </Link>
          </div>

          {/* <div className="font-medium text-[#D1D3D4]">
            الوقت المتبقي {String(seconds).padStart(2, "0")}:{" "}
            {String(minutes).padStart(2, "0")}
          </div> */}
          <button className="text-[#d9ac4f] text-[22px] font-bold flex items-center gap-2">
            <span className="text-[18px] font-medium text-[#d9ac4f]">
              {totalTickets > 0 ? `${totalTickets} x` : ""}
            </span>
            <Image src={ticket} alt="" />
            {t("orderDetails")}{" "}
          </button>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 glass-effect shadow-[0px_0px_26px_0px_rgba(0,0,0,0.12)]  flex items-center justify-center  z-50  lg:hidden">
        <div className="w-full   flex  gap-3  items-center justify-between container mx-auto px-4   py-4">
          <div className="font-medium text-[#D1D3D4] text-[8px] flex flex-col gap-1">
            <span>{t("startsFrom")}</span>
            <span className="text-[18px] font-bold">
              {" "}
              {totalPrice?.toFixed(2)}
            </span>
          </div>

          <Link
            href={"/payment?step=2"}
            onClick={handleSubmit}
            className="bg-[#d9ac4f] text-[#D1D3D4] text-[18px] font-bold rounded-md px-3 py-2  hover:bg-yellow-600 transition-colors"
          >
            {t("chooseTickets")}
          </Link>
        </div>
      </div>
      {showSelectDate && (
        <SelectTicketDate
          ticketDates={eventDetails?.tickets_dates}
          onDateSelect={setSelectedDate}
          onTimeSelect={setSelectedTime}
          time={selectedTime}
          onContinue={async () => {
            const res = await filterTickets({
              event_id: id,
              time_id: selectedTime,
            });

            setTickets(
              (Array.isArray(res?.items) ? res.items : []).map(
                (ticket: any) => ({
                  ...ticket,
                  count: 0, // Initialize count to 0 for each ticket
                }),
              ),
            );
            setShowSelectDate(false);
          }}
        />
      )}
    </div>
  );
}
